import React, {useState, useEffect} from 'react'
import { Divider } from 'antd'
import styled from 'styled-components'

import HealthCard from '../../../components/healthScore/card'
import Information from '../../../components/UI/extraInformation'
import HideWrap from '../../../components/assessmentReview/comp/hideWrap'
import {
  RangeBar,
  ValueIcon
} from '../../../components/assessmentReview/comp/bodyComp'

import { SectionStyle } from '../index.styled'
import { ScoreColorGroup } from '../components/Widgets'
import GripImage from '../../../asserts/images/placeholder/grip-strength.svg'
import SitToStandImage from '../../../asserts/images/placeholder/sit-to-stand.svg'
import SitToStandFemale from '../../../asserts/images/physical-assessment/SittoStandFemale.svg'
import IsometricPullImage from '../../../asserts/images/physical-assessment/ic-isometric-pull.svg'
import IsometricPullFemale from '../../../asserts/images/physical-assessment/IsometricPullFemale.svg'
import IsometricBeltSquatImage from '../../../asserts/images/physical-assessment/ic-isometric-belt-squat.svg'
import IsometricBeltSquatFemale from '../../../asserts/images/physical-assessment/IsometricBeltSquatfemale.svg'
import IsometricPressImage from '../../../asserts/images/physical-assessment/ic-isometric-press.png'
import IsometricPressMale from '../../../asserts/images/physical-assessment/IsometricPressMale.svg'
import AsymmetryChart from '../components/AsymmetryChart'
import classNames from 'classnames'
import { RangeColors } from '../../../utils'
import { handleMetricRanges } from '../../../components/assessmentReview/wrapper'
import { getMetricById } from '../../../api'
import { getRangeAgeScopes } from '../../../api'
import LowLevelMetrics from '../../../components/assessmentReview/comp/LowlevelMetrics'
import { formatDisplayName, getRangeByAgeAndGender } from './LowlevelMetricsHelpers'
import ConditionalHideWrap from '../../../components/assessmentReview/comp/ConditionalHidewrap'

const Container = styled.section`
  ${SectionStyle}

  .ant-divider-horizontal {
    margin: 0;
  }
`

export default function MuscleSection(props) {
  const {
    person,
    hideSectionHandle,
    hideSection,
    compareData,
    dataList,
    view,
    loginRole,
    save,
    onScoreChangeHandle,
    cancelEdit,
    handleCommendSave,
    updateEditingCount,
    ageScopes,
    arDetail: { notes, status },
    data: {
      gripStrength,
      isometricPull,
      isometricPress,
      sitToStand,
      isometricBeltSquat
    }
  } = props

  const [valueResults, setValueResults] = useState([])

  useEffect(() => {
    fetchBodyCompMetricsDetails()
  }, [])

  const fetchBodyCompMetricsDetails = async () => {
    const metricsWithSections = {
      gripStrength: ['combined_grip_strength'],
      isometricPress: ['isometric_press_asymmetry', 'isometric_press_total'],
      isometricPull: ['isometric_pull_asymmetry', 'isometric_pull_total'],
      sitToStand: ['Sit_to_Stand'],
      isometricBeltSquat: ['belt_squat_isometric_asymmetry']
    }

    const ageScopes = (await getRangeAgeScopes()).age_scope

    const biomarkerDetailsPromises = Object.keys(metricsWithSections).flatMap(async (sectionKey) => {
      const section = props.data[sectionKey]

      if (!section) {
        console.warn(`No data found for section: ${sectionKey}`)
        return []
      }

      const sectionPromises = metricsWithSections[sectionKey].map(async (metricKey) => {
        const metric = section[metricKey]

        if (!metric) {
          console.warn(`No data found for metric: ${metricKey} under section: ${sectionKey}`)
          return null
        }

        if (['orange', 'red', 'yellow'].includes(metric.color)) {
          const conventionalRanges = getRangeByAgeAndGender(metric.ranges, person, ageScopes)

          if (metric.metricId) {
            const biomarkerDetail = await getMetricById(metric.metricId, null, 'physical_assessments')

            const introduction = (biomarkerDetail && biomarkerDetail.physical_assessment && biomarkerDetail.physical_assessment.introduction)
              ? biomarkerDetail.physical_assessment.introduction
              : 'No introduction available'

            return {
              displayName: formatDisplayName(metricKey),
              value: metric.value,
              color: metric.color,
              level: metric.level,
              conventionalRanges,
              biomarkerDetail,
              introduction,
              sectionName: 'Muscle Fitness'
            }
          }
        }
        return null
      })

      return Promise.all(sectionPromises)
    })

    const results = await Promise.all(biomarkerDetailsPromises)
    const flatResults = results.flat().filter(result => result !== null) // Flatten the array and remove null values

    setValueResults(flatResults)
  }

  const muscleData =
    dataList.find((item) => item.name === 'Muscle Fitness') || {}
  const muscleCompare =
    compareData.find((item) => item.name === 'Muscle Fitness') || {}

  const muscle = notes.find((item) => item.pillar === 'muscle')
  const gender = person && person.profile && person.profile.gender

  const metrics = [
    {
      name: 'Grip Strength',
      image: GripImage,
      avg: {
        ...gripStrength.grip_strength_avg,
        showRangeArea: true,
        hideName: true,
        hideValueNumber: true
      },
      left: {
        ...gripStrength.grip_strength_left
      },
      right: {
        ...gripStrength.grip_strength_right
      },
      asymmetry: {
        ...gripStrength.grip_strength_diff
      },
      asymmetryText: 'Asymmetry',
      isSymmetricChart: true,
      subMetrics: [
        {
          ...gripStrength.grip_strength_left,
          name: 'Grip Strength (L)'
        },
        {
          ...gripStrength.grip_strength_right,
          name: 'Grip Strength (R)'
        }
      ]
    },
    {
      name: 'Isometric Pull',
      image: gender === 'male' ? IsometricPullImage : IsometricPullFemale,
      imageStyle: {
        width: gender === 'female' && 140
      },
      avg: {
        ...isometricPull.isometric_pull_avg,
        showRangeArea: true,
        hideName: true,
        hideValueNumber: true
      },
      left: {
        ...isometricPull.isometric_pull_left
      },
      right: {
        ...isometricPull.isometric_pull_right
      },
      asymmetry: {
        ...isometricPull.isometric_pull_asymmetry
      },
      asymmetryText: 'Asymmetry',
      isSymmetricChart: true,
      subMetrics: [
        {
          ...isometricPull.isometric_pull_left,
          name: 'Isometric Pull (L)'
        },
        {
          ...isometricPull.isometric_pull_right,
          name: 'Isometric Pull (R)'
        },
        {
          ...isometricPull.isometric_pull_total,
          name: 'Isometric Pull/ BW (Total)'
        }
      ]
    },
    {
      name: 'Isometric Press',
      image: gender === 'male' ? IsometricPressMale : IsometricPressImage,
      imageStyle: {
        width: 180
      },
      avg: {
        ...isometricPress.isometric_press_avg,
        showRangeArea: true,
        hideName: true,
        hideValueNumber: true
      },
      left: {
        ...isometricPress.isometric_press_left
      },
      right: {
        ...isometricPress.isometric_press_right
      },
      asymmetry: {
        ...isometricPress.isometric_press_asymmetry
      },
      asymmetryText: 'Asymmetry',
      isSymmetricChart: true,
      subMetrics: [
        {
          ...isometricPress.isometric_press_left,
          name: 'Isometric Press (L)'
        },
        {
          ...isometricPress.isometric_press_right,
          name: 'Isometric Press (R)'
        },
        {
          ...isometricPress.isometric_press_total,
          name: 'Isometric Press/ BW (Total)'
        }
      ]
    },
    {
      name: 'Sit to Stand',
      image: gender === 'male' ? SitToStandImage : SitToStandFemale,
      imageStyle: {
        width: 120
      },
      avg: {
        ...sitToStand.Sit_to_Stand,
        showRangeArea: true,
        hideName: true
      },
      asymmetry: {
        ...sitToStand.knee_flexion_asymmetry
      },
      asymmetryText: 'Knee Flexion Asymmetry',
      noCompare: true,
      // isSymmetricChart: false,
      subMetrics: [
        {
          ...sitToStand.Sit_to_Stand,
          name: 'Sit to stand'
        },
        {
          ...sitToStand.sit_to_stand_age_ranked,
          name: 'Sit to Stand (Age-ranked)'
        }
      ]
    },
    {
      name: 'Isometric Belt Squat ',
      image: gender === 'male' ? IsometricBeltSquatImage : IsometricBeltSquatFemale,
      imageStyle: {
        width: gender === 'male' ? 160 : 140
      },
      avg: {
        ...isometricBeltSquat.belt_squat_isometric_pull,
        showRangeArea: true,
        hideName: true
      },
      asymmetry: {
        ...isometricBeltSquat.belt_squat_isometric_asymmetry
      },
      asymmetryText: 'Isometric Belt Squat Peak Force/ BW Asymmetry',
      noCompare: true,
      subMetrics: [
        {
          ...isometricBeltSquat.belt_squat_isometric_pull,
          name: 'Isometric Belt Squat Peak Force/ BW - Total'
        }
      ]
    }
  ]

  return (
    <ConditionalHideWrap
      name="Muscle Fitness"
      isSection={true}
      syncStatus={hideSectionHandle}
      status={hideSection}
      view={view}
      condition={
        view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Muscle Fitness'))
      }
    >
      <Container>
        <h1 className="ar-section-title">Muscle Strength and Fitness</h1>
        <ConditionalHideWrap
          name="Muscle Fitness - summary"
          title="summary"
          syncStatus={hideSectionHandle}
          status={hideSection}
          view={view}
          isHealthRisk={true}
          condition={
            view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Muscle Fitness - summary'))
          }
        >
          <h3 className="ar-section-card-title">Summary</h3>
          <HealthCard
            {...muscleData}
            compare={muscleCompare}
            save={save}
            loginRole={loginRole}
            status={view === 'Draft' ? 'Draft' : 'Opened'}
            onChangeHandle={onScoreChangeHandle}
            disEdit={status === 1 || view === 'Preview'}
            cancel={cancelEdit}
          />
          <Information
            title="COMMENT/NOTE"
            disMarkDone={true}
            initialValue={muscle ? muscle.memo : ''}
            status={view === 'Draft' ? 'Draft' : 'Opened'}
            updateCommend={(value, status) =>
              handleCommendSave(value, 'muscle', status)
            }
            updateEditingCount={updateEditingCount}
          />
        </ConditionalHideWrap>

        <ScoreColorGroup domain={'muscle'} />
        {metrics.map((metric, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider />}
            <MuscleMetric
              {...metric}
              {...{
                person,
                ageScopes
              }}
            />
          </React.Fragment>
        ))}
        <LowLevelMetrics metricValues={valueResults} hideSection={hideSection} view={view} hideSectionHandle={hideSectionHandle} />
      </Container>
    </ConditionalHideWrap>
  )
}

const MetricContainer = styled.div`
  height: 500px;
  position: relative;

  .metric-name {
    font-size: 18px;
    letter-spacing: 0.3px;
    line-height: 27px;
    position: absolute;
    top: 68px;
    color: #4c6072;
  }
  .metric-content {
    display: flex;
    align-items: center;
    height: 100%;
    & > div {
      display: inline-block;
    }

    .metric-middle-container {
      display: flex;
      flex-direction: column;
      padding-top: 40px;
      .bar-with-title {
        display: flex;
        & > span {
          width: 40px;
          min-width: 40px;
          font-size: 12px;
          text-align: center;
        }
        & > div {
          flex-grow: 1;
        }
        .ranges {
          margin-top: 6px;
        }
      }

      .metric-text-container {
        display: flex;
        flex-wrap: wrap;
        row-gap: 24px;
        margin-left: 40px;
        margin-top: 36px;
        .single-text-metric {
          margin-right: 75px;
          .single-text-metric-title {
            font-size: 15px;
            color: #4c6072;
          }
          .single-text-metric-content {
            font-size: 24px;
            color: #40c47c;
            font-weight: 700;
          }
        }

        &.no-compare {
          margin-left: 0;
        }
      }
    }
  }
  .metric-polar-chart-container {
    position: absolute;
    width: 340px;
    top: 50%;
    right: -75px;
    transform: translateY(-50%);
    .polar-chart-name {
      font-size: 15px;
      line-height: 21px;
      color: #4c6072;
      display: flex;
      text-align: center;
      justify-content: center;
      & > div {
        max-width: 220px;
      }
    }
  }
`

function MuscleMetric(props) {
  const { noCompare, person, ageScopes, isSymmetricChart } = props
  return (
    <MetricContainer>
      <div className="metric-name">{props.name}</div>
      <div className="metric-content">
        <div
          className="metric-image-conatiner"
          style={{
            width: '22.57%'
          }}
        >
          <img src={props.image} alt="bg" style={props.imageStyle || {}} />
        </div>
        <div
          className="metric-middle-container"
          style={{
            width: '53.57%'
          }}
        >
          {noCompare ? (
            <RangeBar {...props.avg} {...{ ageScopes, person }} />
          ) : (
            <>
              <div className="bar-with-title">
                <span>(L)</span>
                <CompareLine {...props.left} {...{ ageScopes, person }} />
              </div>
              <div className="bar-with-title">
                <span>(R)</span>
                <CompareLine {...props.right} {...{ ageScopes, person }} />
              </div>
              <div className="bar-with-title">
                  {props.name === 'Isometric Pull' || props.name === 'Isometric Press' || props.name === 'Grip Strength' ? null : <span>(AVG)</span>}
                  <RangeBar {...props.avg} {...{ ageScopes, person }} hideValueIcon={props.name === 'Isometric Pull' || props.name === 'Isometric Press' || props.name === 'Grip Strength'} />
              </div>
            </>
          )}
          <div
            className={classNames('metric-text-container', {
              'no-compare': noCompare
            })}
          >
            {props.subMetrics.map((metric, index) => {
              return (
                <div className="single-text-metric" key={index}>
                  <div className="single-text-metric-title">{metric.name}</div>
                  <div
                    className="single-text-metric-content"
                    style={{
                      color: RangeColors[metric.color] || 'rgba(67, 67, 67, 1)'
                    }}
                  >
                    {metric.value
                      ? `${metric.value +
                          (metric.unit ? ` ${metric.unit}` : '')}`
                      : '--'}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div className="metric-polar-chart-container">
        <div className="polar-chart-name">
          <div>{props.asymmetryText}</div>
        </div>
        <AsymmetryChart
          metric={props.asymmetry}
          {...{ ageScopes, person, isSymmetricChart }}
        />
      </div>
    </MetricContainer>
  )
}

const CompareLineContainer = styled.div`
  /* position: relative; */
  display: flex;
  height: 22px;
  padding-bottom: 5px;

  .range-item {
    position: relative;
    .value {
      position: absolute;
      bottom: -7px;
    }
    & > svg {
      margin-bottom: 5px;
    }
  }
`

function CompareLine(props) {
  let { value, ranges, ageScopes, person, level } = props

  let _ranges = handleMetricRanges(ranges, person, ageScopes)

  return (
    <CompareLineContainer>
      {_ranges.map((item, index) => (
        <div
          key={index}
          className={`${item.name} range-item`}
          style={{
            width: 100 / _ranges.length + '%'
          }}
        >
          <svg width="100%" height="2" xmlns="http://www.w3.org/2000/svg">
            <line
              x1="0"
              y1="0"
              x2="1000"
              stroke="#E1DEDE"
              strokeWidth="2"
              strokeDasharray="2,2"
            />
          </svg>
          {level === item.name && (
            <>
              <ValueIcon
                color={item.color || '#8A969F'}
                style={getPointExtraStyle(value, item)}
                hideValueNumber={true}
              />
            </>
          )}
        </div>
      ))}
    </CompareLineContainer>
  )

  function getPointExtraStyle(value, range) {
    let left = 50
    let correctionOffset = -50
    if (range.max && range.min) {
      left = ((value - range.min) / (range.max - range.min)) * 100
    }
    if (Math.round(left) === 0) {
      correctionOffset = 0
    } else if (Math.round(left) === 100) {
      correctionOffset = -100
    }
    return {
      left: left + '%',
      transform: `translateX(${correctionOffset}%)`
    }
  }
}
